import { useEffect, useMemo, useRef, useState } from "react";
import type { PdfProvenanceProps, Rect, TextItem } from "./types";
import { findAllMatches } from "./textMatch";
import { isRenderCancellation, usePdfDocument } from "./usePdfDocument";
import "./PdfProvenance.css";

const STAGGER_MS = 120;
const SWEEP_HOLD_MS = 1500;
const SWEEP_FADE_MS = 250; // must match .pdfprov-hl--sweep's transition duration in PdfProvenance.css
const THUMB_WIDTH = 64;
// Demo documents are 1-3 pages; this is a guard against a pathological
// upload, not a feature. Pages beyond the cap simply don't get a filmstrip
// tile — the "+N more pages" tile below stands in for them, non-clickable
// (there's no way to jump straight to an unrendered page's thumbnail, and
// building one just to make it clickable is more machinery than a guard
// case warrants).
const THUMB_PAGE_CAP = 12;
/** Hover zoom: the highlighted value grows to about this share of the page
 *  view, never past ZOOM_MAX. Below ZOOM_MIN it is already readable. */
const ZOOM_MAX = 3;
const ZOOM_MIN = 1.2;
const ZOOM_FILL = 0.6;
/** Backing store cap, so a zoomed page stays sharp without a huge canvas. */
const MAX_CANVAS_PX = 4096;
/** Leaving one value for the next passes through "nothing hovered"; hold the
 *  zoom that long so moving along a row does not bounce out and back in. */
const ZOOM_OUT_DELAY_MS = 300;

export interface Zoom {
  scale: number;
  x: number;
  y: number;
}

/**
 * Scale and pan (in px, transform-origin top left) that bring `rects` to about
 * ZOOM_FILL of a `width` x `height` page view, clamped so the page always
 * covers the view. Null when the rects are already big enough to read.
 */
export function zoomFor(rects: Rect[], width: number, height: number): Zoom | null {
  if (rects.length === 0 || width <= 0 || height <= 0) return null;
  const x0 = Math.min(...rects.map((r) => r.x));
  const y0 = Math.min(...rects.map((r) => r.y));
  const x1 = Math.max(...rects.map((r) => r.x + r.w));
  const y1 = Math.max(...rects.map((r) => r.y + r.h));
  const scale = Math.min(ZOOM_MAX, ZOOM_FILL / Math.max(x1 - x0, 0.001), ZOOM_FILL / Math.max(y1 - y0, 0.001));
  if (scale < ZOOM_MIN) return null;
  const clamp = (v: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, v));
  const cx = ((x0 + x1) / 2) * width;
  const cy = ((y0 + y1) / 2) * height;
  return {
    scale,
    x: clamp(width / 2 - cx * scale, width - width * scale, 0),
    y: clamp(height / 2 - cy * scale, height - height * scale, 0),
  };
}

function usePrefersReducedMotion(): boolean {
  const [reduced, setReduced] = useState(
    () => typeof window !== "undefined" && !!window.matchMedia?.("(prefers-reduced-motion: reduce)").matches
  );
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const handler = () => setReduced(mq.matches);
    mq.addEventListener?.("change", handler);
    return () => mq.removeEventListener?.("change", handler);
  }, []);
  return reduced;
}

/**
 * Renders the actually-dropped PDF and, on request, highlights the parts
 * of the page where extracted values were read from — by searching the
 * PDF's own text layer (see ./textMatch), never by rendering any
 * placeholder/sample content.
 */
export default function PdfProvenance({
  file,
  highlights,
  sweep = false,
  sweepQueries,
  docLabel,
  className,
}: PdfProvenanceProps) {
  const { status, pageCount, hasTextLayer, renderPage, getTextItems } = usePdfDocument(file);
  const reducedMotion = usePrefersReducedMotion();

  // Measured for fit-width: the page *slot*, not the outer card. The card
  // adds its own padding, the grey tray adds more, and the filmstrip eats a
  // fixed column — measuring the root would render the page ~140px wider
  // than the space it has to sit in, and would miss the reflow when the
  // filmstrip appears on a multi-page document.
  const pageSlotRef = useRef<HTMLDivElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const thumbRefs = useRef<Map<number, HTMLCanvasElement>>(new Map());
  const currentPageRef = useRef(1);

  const [currentPage, setCurrentPage] = useState(1);
  const [containerWidth, setContainerWidth] = useState(0);
  const [renderedSize, setRenderedSize] = useState<{ width: number; height: number } | null>(null);
  const [matchesByPage, setMatchesByPage] = useState<Map<number, Map<string, Rect[]>>>(new Map());
  const [sweepOn, setSweepOn] = useState(false);
  // The page number the LATEST requested main-page render actually failed
  // to paint (a genuine failure, not a cancellation/supersession — see the
  // render effect below), or null when nothing's failed. Thumbnails aren't
  // tracked here: a bad thumbnail silently drops out of the filmstrip
  // instead, so one broken page never blocks the rest of it.
  const [renderFailedPage, setRenderFailedPage] = useState<number | null>(null);
  const [zoom, setZoom] = useState<Zoom | null>(null);

  useEffect(() => {
    currentPageRef.current = currentPage;
  }, [currentPage]);

  // A newly-dropped file always starts back at page 1.
  useEffect(() => {
    setCurrentPage(1);
    setRenderedSize(null);
    setRenderFailedPage(null);
  }, [file]);

  // --- measure the container so the page can fit its width ---
  useEffect(() => {
    const el = pageSlotRef.current;
    if (!el) return;
    const measure = () => setContainerWidth(el.clientWidth);
    measure();
    if (typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", measure);
      return () => window.removeEventListener("resize", measure);
    }
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // --- render the current page whenever the page, size, or doc changes ---
  useEffect(() => {
    if (status !== "ready" || !canvasRef.current || containerWidth <= 0) return;
    let cancelled = false;
    const pageForThisRender = currentPage;
    const dpr = window.devicePixelRatio || 1;
    const pixelScale = Math.max(1, Math.min(ZOOM_MAX, MAX_CANVAS_PX / (containerWidth * dpr)));
    renderPage(currentPage, canvasRef.current, containerWidth, pixelScale)
      .then((size) => {
        if (cancelled) return;
        setRenderedSize(size);
        setRenderFailedPage(null);
      })
      .catch((err) => {
        // `cancelled` is set by this effect's own cleanup the instant a
        // newer render is requested (page/size/doc change), so by
        // construction only the LATEST requested render's rejection can
        // reach here. isRenderCancellation additionally filters out the
        // cancel/supersede rejections the render queue produces as part of
        // normal operation (see usePdfDocument) — what's left is a genuine
        // paint failure, worth telling the user about. The previous frame
        // (or a blank canvas) stays up underneath the message; `status`
        // from the hook remains the source of truth for the
        // document-level "couldn't render" fallback.
        if (cancelled || isRenderCancellation(err)) return;
        setRenderFailedPage(pageForThisRender);
      });
    return () => {
      cancelled = true;
    };
  }, [status, currentPage, containerWidth, renderPage]);

  // --- render each filmstrip thumbnail once the doc is ready ---
  useEffect(() => {
    if (status !== "ready" || pageCount <= 1) return;
    let cancelled = false;
    (async () => {
      const thumbPageCount = Math.min(pageCount, THUMB_PAGE_CAP);
      for (let p = 1; p <= thumbPageCount; p++) {
        if (cancelled) return;
        const canvas = thumbRefs.current.get(p);
        if (!canvas) continue;
        try {
          await renderPage(p, canvas, THUMB_WIDTH);
        } catch {
          // A bad thumbnail shouldn't block the rest of the filmstrip.
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [status, pageCount, renderPage]);

  // The full set of values we ever need match data for: whatever's
  // currently hovered, plus the full sweep set (needed for the
  // filmstrip's per-page match dots even before a sweep is running).
  const activeQueries = useMemo(() => {
    const set = new Set<string>();
    (highlights ?? []).forEach((q) => set.add(q));
    (sweepQueries ?? []).forEach((q) => set.add(q));
    return Array.from(set);
  }, [highlights, sweepQueries]);
  const activeQueriesKey = activeQueries.join("␟");

  // --- find matches for every active query, on every page ---
  useEffect(() => {
    if (status !== "ready" || !hasTextLayer || activeQueries.length === 0) {
      setMatchesByPage(new Map());
      return;
    }
    let cancelled = false;
    (async () => {
      const next = new Map<number, Map<string, Rect[]>>();
      for (let p = 1; p <= pageCount; p++) {
        const items: TextItem[] = await getTextItems(p);
        if (cancelled) return;
        next.set(p, findAllMatches(items, activeQueries));
      }
      if (!cancelled) setMatchesByPage(next);
    })();
    return () => {
      cancelled = true;
    };
    // activeQueriesKey (not activeQueries) is the real dependency — the
    // array identity changes every render even when its contents don't.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, hasTextLayer, pageCount, activeQueriesKey, getTextItems]);

  const highlightsKey = (highlights ?? []).join("␟");

  // --- jump to the first page that actually has a requested highlight ---
  // Keyed on the highlights *value* (not currentPage) via a ref for the
  // current page, so that once we've landed on a page for a given hover
  // value, a manual filmstrip click isn't immediately fought by this
  // effect re-running.
  useEffect(() => {
    if (!highlights || highlights.length === 0 || matchesByPage.size === 0) return;
    const cur = currentPageRef.current;
    const pageHasAny = (p: number) => highlights.some((q) => (matchesByPage.get(p)?.get(q)?.length ?? 0) > 0);
    if (pageHasAny(cur)) return;
    for (let p = 1; p <= pageCount; p++) {
      if (pageHasAny(p)) {
        setCurrentPage(p);
        return;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [highlightsKey, matchesByPage, pageCount]);

  // sweepQueries is very often a fresh array literal from the parent. Keyed
  // on identity, the sweep effect would re-run on every parent render,
  // resetting sweepOn and restarting the timers — the sweep would never
  // finish playing.
  const sweepQueriesKey = (sweepQueries ?? []).join("\u241f");
  // A caller can legitimately pass the same value twice: the AD interval
  // hover sends period_unit AND warning_unit, both often "Hours". One rect
  // per OCCURRENCE stacks translucent highlights on the same span, so that
  // value reads visibly darker than every other highlight on the page.
  // Rects are always drawn off the distinct set. (activeQueries above
  // already dedupes for match LOOKUP; the two flatMaps below are the render
  // sites, and sweepCount here feeds the stagger span.)
  const uniqueHighlights = highlights ? Array.from(new Set(highlights)) : null;
  const uniqueSweepQueries = sweepQueries ? Array.from(new Set(sweepQueries)) : undefined;
  const sweepCount = uniqueSweepQueries?.length ?? 0;

  // --- one-shot sweep: stagger all sweepQueries in, hold, then fade ---
  useEffect(() => {
    if (!sweep) {
      setSweepOn(false);
      return;
    }
    if (reducedMotion) {
      setSweepOn(true);
      const t = setTimeout(() => setSweepOn(false), SWEEP_HOLD_MS);
      return () => clearTimeout(t);
    }
    // Start off, then flip on next frame — toggling in the same tick a
    // sweep starts can get coalesced into one paint with nothing to
    // transition from, i.e. no visible fade-in.
    setSweepOn(false);
    const raf = requestAnimationFrame(() => setSweepOn(true));
    const staggerSpan = Math.max(0, sweepCount - 1) * STAGGER_MS;
    const t = setTimeout(() => setSweepOn(false), staggerSpan + SWEEP_FADE_MS + SWEEP_HOLD_MS);
    return () => {
      cancelAnimationFrame(raf);
      clearTimeout(t);
    };
  }, [sweep, sweepQueriesKey, sweepCount, reducedMotion]);

  const currentMatches = matchesByPage.get(currentPage);

  const highlightRects: Rect[] =
    uniqueHighlights && currentMatches ? uniqueHighlights.flatMap((q) => currentMatches.get(q) ?? []) : [];

  // Zoom follows the hover highlights only; the sweep never zooms.
  const target = renderedSize ? zoomFor(highlightRects, renderedSize.width, renderedSize.height) : null;
  const targetKey = target ? `${target.scale}:${target.x}:${target.y}` : "";
  useEffect(() => {
    if (target) {
      setZoom(target);
      return undefined;
    }
    const timer = setTimeout(() => setZoom(null), ZOOM_OUT_DELAY_MS);
    return () => clearTimeout(timer);
    // targetKey stands in for `target`, which is a new object every render
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targetKey]);

  // Mounted as soon as `sweep` is on — NOT gated on sweepOn. An element
  // inserted with .pdfprov-hl--on already applied has no previous opacity
  // to transition from, so it snaps to full and both the fade and the
  // per-query stagger are lost. Mounting at opacity 0 first, then adding
  // --on on the next frame, is what makes transition-delay do anything.
  const sweepEntries: Array<{ rect: Rect; delay: number }> =
    sweep && uniqueSweepQueries && currentMatches
      ? uniqueSweepQueries.flatMap((q, i) =>
          (currentMatches.get(q) ?? []).map((rect) => ({ rect, delay: reducedMotion ? 0 : i * STAGGER_MS }))
        )
      : [];

  const rectStyle = (rect: Rect, size: { width: number; height: number }) => ({
    left: `${rect.x * size.width}px`,
    top: `${rect.y * size.height}px`,
    width: `${Math.max(2, rect.w * size.width)}px`,
    height: `${Math.max(2, rect.h * size.height)}px`,
  });

  if (status === "failed") {
    return (
      <div className={`pdfprov-root pdfprov-root--failed ${className ?? ""}`}>
        <div className="pdfprov-fallback">
          <div className="pdfprov-fallback__title">Couldn&apos;t render this document</div>
          {docLabel ? <div className="pdfprov-fallback__label">{docLabel}</div> : null}
        </div>
      </div>
    );
  }

  return (
    <div className={`pdfprov-root ${className ?? ""}`}>
      <div className="pdfprov-head">
        <span className="pdfprov-head__label">{docLabel ?? "Source document"}</span>
        <span className="pdfprov-head__page">
          {status === "ready" ? `page ${currentPage} of ${pageCount}` : "loading…"}
        </span>
      </div>

      <div className="pdfprov-body">
        {pageCount > 1 ? (
          <div className="pdfprov-filmstrip">
            {Array.from({ length: Math.min(pageCount, THUMB_PAGE_CAP) }, (_, i) => i + 1).map((p) => {
              const hasDot = activeQueries.some((q) => (matchesByPage.get(p)?.get(q)?.length ?? 0) > 0);
              return (
                <button
                  key={p}
                  type="button"
                  className={`pdfprov-thumb${p === currentPage ? " pdfprov-thumb--active" : ""}`}
                  onClick={() => setCurrentPage(p)}
                  aria-label={`Page ${p}`}
                  aria-current={p === currentPage}
                >
                  <canvas
                    ref={(el) => {
                      if (el) thumbRefs.current.set(p, el);
                      else thumbRefs.current.delete(p);
                    }}
                  />
                  {hasDot ? <span className="pdfprov-thumb__dot" /> : null}
                </button>
              );
            })}
            {pageCount > THUMB_PAGE_CAP ? (
              <div className="pdfprov-thumb-more" aria-hidden="true">
                +{pageCount - THUMB_PAGE_CAP} more page{pageCount - THUMB_PAGE_CAP === 1 ? "" : "s"}
              </div>
            ) : null}
          </div>
        ) : null}

        <div className="pdfprov-page" ref={pageSlotRef}>
          {renderFailedPage === currentPage ? (
            <div className="pdfprov-page-error">Couldn&apos;t render page {currentPage}</div>
          ) : null}
          <div
            className={`pdfprov-stage${!reducedMotion ? " pdfprov-stage--animated" : ""}`}
            data-testid="pdfprov-stage"
            style={{
              transform: zoom ? `translate(${zoom.x}px, ${zoom.y}px) scale(${zoom.scale})` : undefined,
            }}
          >
            <canvas ref={canvasRef} className="pdfprov-canvas" />
            {renderedSize ? (
              <div className="pdfprov-overlay" style={{ width: renderedSize.width, height: renderedSize.height }}>
                {sweepEntries.map(({ rect, delay }, i) => (
                  <div
                    key={`sweep-${i}`}
                    className={`pdfprov-hl pdfprov-hl--sweep${sweepOn ? " pdfprov-hl--on" : ""}`}
                    style={{ ...rectStyle(rect, renderedSize), transitionDelay: `${delay}ms` }}
                  />
                ))}
                {highlightRects.map((rect, i) => (
                  <div key={`hl-${i}`} className="pdfprov-hl pdfprov-hl--on" style={rectStyle(rect, renderedSize)} />
                ))}
              </div>
            ) : null}
          </div>
        </div>
      </div>

      {status === "ready" && !hasTextLayer ? (
        <div className="pdfprov-hint">Scanned document — no text layer to point at</div>
      ) : null}
    </div>
  );
}
